/**
 * Photo upload for an entry: binary to S3/MinIO via the template's storage
 * endpoint + synchronized attachment.entry_id row.
 */
import { createSignal } from "solid-js";
import { msg } from "@lingui/core/macro";
import { addEntryPhotoMutation, MAX_ATTACHMENT_SIZE_BYTES } from "@journal/shared";
import { uploadStorageObject } from "@/lib/api";
import { showToast } from "@/components/ui/toast";
import { useI18n } from "@/providers/i18n-provider";
import { useJournalData } from "./use-journal-data";

export function usePhotoUpload() {
  const { drizzle, userId } = useJournalData();
  const { t } = useI18n();
  const [isUploading, setIsUploading] = createSignal(false);

  const uploadPhotos = async (entryId: string, files: readonly File[]): Promise<number> => {
    const db = drizzle();
    const uid = userId();
    if (!db || !uid || files.length === 0) {
      return 0;
    }

    setIsUploading(true);
    let uploaded = 0;
    try {
      for (const file of files) {
        if (file.size > MAX_ATTACHMENT_SIZE_BYTES) {
          showToast(t(msg`{name} is too large.`, { name: file.name }), "error");
          continue;
        }
        try {
          const { url } = await uploadStorageObject(file);
          await addEntryPhotoMutation({
            db,
            userId: uid,
            entryId,
            filename: file.name,
            url,
            contentType: file.type || "application/octet-stream",
            size: file.size,
          });
          uploaded += 1;
        } catch (error) {
          console.error("Error uploading photo:", error);
          showToast(t(msg`Failed to upload photo. Please try again.`), "error");
        }
      }
    } finally {
      setIsUploading(false);
    }
    return uploaded;
  };

  return { uploadPhotos, isUploading };
}
